<template>
  <div class="rk-book">
    <div class="rk-container">
      <div class="rk-book__card">
        <div class="rk-kicker">
          <span class="rk-kicker__rule"></span>
          <span class="rk-kicker__text"><i class="bi bi-calendar-check"></i> Book a demo</span>
        </div>

        <template v-if="step === 'form'">
          <h1>See Rental Kenya on your own portfolio</h1>
          <p class="rk-book__sub">
            Tell us a bit about your properties and we'll set up a demo tailored to how you actually manage them.
          </p>

          <form @submit.prevent="submitForm" class="rk-book__form">
            <div class="rk-field">
              <label>Full name</label>
              <input v-model="form.full_name" type="text" placeholder="Jane Wanjiru" required :disabled="loading">
            </div>

            <div class="rk-field">
              <label>Business name <span class="rk-field__optional">(optional)</span></label>
              <input v-model="form.business_name" type="text" placeholder="Pacific Crest Properties" :disabled="loading">
            </div>

            <div class="rk-field-row">
              <div class="rk-field">
                <label>Email</label>
                <input v-model="form.email" type="email" placeholder="you@example.com" required :disabled="loading">
              </div>
              <div class="rk-field">
                <label>Phone number</label>
                <input v-model="form.phone_number" type="tel" placeholder="0712 345 678" required :disabled="loading">
              </div>
            </div>

            <div class="rk-field">
              <label>How many units do you manage?</label>
              <div class="rk-radio-group">
                <label
                  v-for="range in unitRanges"
                  :key="range"
                  class="rk-radio"
                  :class="{ 'is-active': form.units_range === range }"
                >
                  <input type="radio" :value="range" v-model="form.units_range" :disabled="loading">
                  <span>{{ range }}</span>
                </label>
              </div>
            </div>

            <div class="rk-field">
              <label>Anything specific you'd like us to cover? <span class="rk-field__optional">(optional)</span></label>
              <textarea v-model="form.message" rows="3" placeholder="e.g. multiple properties, M-Pesa reconciliation, bulk SMS..." :disabled="loading"></textarea>
            </div>

            <p v-if="errorMessage" class="rk-form-error">
              <i class="bi bi-exclamation-circle"></i> {{ errorMessage }}
            </p>

            <button type="submit" class="rk-btn rk-btn--primary rk-btn--block" :disabled="loading">
              <span v-if="loading"><i class="bi bi-arrow-repeat rk-spin"></i> Sending…</span>
              <span v-else>Book my demo</span>
            </button>
          </form>
        </template>

        <template v-else>
          <div class="rk-book__success-icon"><i class="bi bi-check-circle"></i></div>
          <h1>Request received</h1>
          <p class="rk-book__sub">
            Thanks, {{ form.full_name.split(' ')[0] }} — we'll reach out within 24 hours to schedule your demo.
            We've also sent a confirmation to <strong>{{ form.email }}</strong>.
          </p>
          <router-link to="/" class="rk-btn rk-btn--primary">
            Back to homepage
          </router-link>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { apiFetch } from "@/services/api.js"

const unitRanges = ["1-10", "11-50", "51+"]

const form = ref({
  full_name: "",
  business_name: "",
  email: "",
  phone_number: "",
  units_range: "",
  message: "",
})

const step = ref("form") // 'form' | 'success'
const loading = ref(false)
const errorMessage = ref("")

async function submitForm() {
  errorMessage.value = ""

  if (!form.value.units_range) {
    errorMessage.value = "Please select how many units you manage."
    return
  }

  loading.value = true
  try {
    await apiFetch("/book-demo", {
      method: "POST",
      body: JSON.stringify(form.value),
    })
    step.value = "success"
  } catch (err) {
    errorMessage.value = err.message || "Something went wrong. Please try again."
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.rk-container { max-width: 640px; margin: 0 auto; padding: 0 1.5rem; }

.rk-book {
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 6rem 0;
  background: var(--paper);
}

.rk-book__card {
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  padding: 3rem;
  width: 100%;
}

.rk-kicker { display: flex; align-items: center; gap: 0.85rem; margin-bottom: 1.5rem; }
.rk-kicker__rule { width: 32px; height: 3px; background: var(--brass); border-radius: 2px; flex-shrink: 0; }
.rk-kicker__text {
  font-family: var(--font-mono); font-size: 0.85rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase; color: var(--brass);
  display: inline-flex; align-items: center; gap: 0.5rem;
}

h1 {
  font-family: var(--font-display); font-weight: 400; font-size: 2rem;
  color: var(--ink); margin: 0 0 0.75rem; line-height: 1.2;
}

.rk-book__sub {
  font-family: var(--font-body); color: var(--slate); font-size: 1rem;
  line-height: 1.6; margin: 0 0 2rem;
}
.rk-book__sub strong { color: var(--ink); }

.rk-book__form { display: flex; flex-direction: column; gap: 1.25rem; }

.rk-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }

.rk-field { display: flex; flex-direction: column; gap: 0.5rem; }
.rk-field label {
  font-family: var(--font-body); font-size: 0.85rem; font-weight: 600; color: var(--ink);
}
.rk-field__optional { font-weight: 400; color: var(--slate-soft); }

.rk-field input,
.rk-field textarea {
  font-family: var(--font-body); font-size: 0.95rem; color: var(--ink);
  border: 1.5px solid var(--line); border-radius: var(--radius-sm);
  padding: 0.75rem 1rem; background: var(--paper);
  transition: all 0.2s var(--ease); resize: vertical;
}
.rk-field input:focus,
.rk-field textarea:focus {
  outline: none; border-color: var(--brass); background: #fff;
  box-shadow: 0 0 0 3px rgba(176,141,63,0.12);
}
.rk-field input:disabled,
.rk-field textarea:disabled { opacity: 0.6; }

.rk-radio-group { display: flex; gap: 0.6rem; flex-wrap: wrap; }
.rk-radio {
  display: inline-flex; align-items: center; gap: 0.5rem;
  border: 1.5px solid var(--line); border-radius: 100px;
  padding: 0.55rem 1.1rem; cursor: pointer; transition: all 0.2s var(--ease);
  font-family: var(--font-body); font-size: 0.88rem; color: var(--slate);
}
.rk-radio input { position: absolute; opacity: 0; pointer-events: none; }
.rk-radio.is-active { background: var(--ink); border-color: var(--ink); color: var(--paper); }

.rk-form-error {
  display: flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-body); font-size: 0.88rem; color: var(--rust);
  background: rgba(196,86,47,0.08); border: 1px solid rgba(196,86,47,0.2);
  border-radius: var(--radius-sm); padding: 0.75rem 1rem; margin: 0;
}

.rk-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.6rem;
  font-family: var(--font-body); font-weight: 600; font-size: 0.98rem;
  border: none; border-radius: var(--radius-sm); cursor: pointer;
  padding: 0.9rem 1.5rem; transition: all 0.2s var(--ease);
}
.rk-btn--primary { background: var(--brass); color: var(--ink); }
.rk-btn--primary:hover:not(:disabled) { background: var(--brass-light); transform: translateY(-1px); }
.rk-btn--primary:disabled { opacity: 0.65; cursor: not-allowed; }
.rk-btn--block { width: 100%; }

.rk-spin { display: inline-block; animation: rk-spin 0.7s linear infinite; }
@keyframes rk-spin { to { transform: rotate(360deg); } }

.rk-book__success-icon {
  width: 64px; height: 64px; border-radius: 50%;
  background: rgba(79,122,107,0.15); color: var(--sage);
  display: flex; align-items: center; justify-content: center;
  font-size: 2rem; margin-bottom: 1.5rem;
}

@media (max-width: 560px) {
  .rk-book__card { padding: 2rem 1.5rem; }
  .rk-field-row { grid-template-columns: 1fr; }
}
</style>